import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import {
  Visibility,
  getPath,
  isRecord,
  itemVisibility,
  setPath,
  validateField,
  visibilityOf,
} from './cardModel';
import {
  CardHandlers,
  FieldErrors,
  FieldType,
  FormField,
  FormOption,
} from './cardTypes';
import {
  EmptyLine,
  FieldCell,
  FieldGrid,
  FieldHint,
  FieldNote,
  FieldSet,
  HoursRow,
  LinkButton,
  LoadProblem,
  NotSet,
  OptionDetail,
  OptionLabel,
  ReadOnlyList,
  RowCard,
  RowHeader,
  SecondaryButton,
  mutedText,
} from './cardStyles';
import {
  NOT_IN_LIST_TEXT,
  OptionLabels,
  WEEK_DAYS,
  dayLabel,
  displayLines,
  ownLabels,
} from './fieldDisplay';

export type LoadOptions = CardHandlers['loadOptions'];

export interface FieldRendererProps {
  field: FormField;
  value: unknown;
  errors: FieldErrors;
  errorKey?: string;
  hint?: string;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  loadOptions: LoadOptions;
  onOptions?: (field: FormField, options: FormOption[]) => void;
  scope?: Visibility;
  prefill?: Record<string, unknown>;
}

interface InputProps {
  field: FormField;
  value: unknown;
  error?: string;
  hint?: string;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  onBlur?: () => void;
}

interface OptionInputProps extends InputProps {
  loadOptions: LoadOptions;
  onOptions?: (field: FormField, options: FormOption[]) => void;
}

interface HoursValue {
  day: string;
  start: string;
  end: string;
}

export const MAX_OPTIONS = 200;
const SEARCH_DELAY_MS = 250;

const WIDE_TYPES: FieldType[] = [
  'textarea',
  'multiselect',
  'numberList',
  'weeklyHours',
  'repeater',
  'userPicker',
  'entityPicker',
  'note',
];

const TEXT_TYPES: Partial<Record<FieldType, string>> = {
  phone: 'tel',
  email: 'email',
  date: 'date',
  time: 'time',
};

const AUTOCOMPLETE: Partial<Record<FieldType, string>> = {
  phone: 'tel',
  email: 'email',
};

let rowSeed = 0;

const nextRowKey = (): string => {
  rowSeed += 1;
  return `alpha-row-${rowSeed}`;
};

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value == null || value === '' ? [] : [value];
};

const toText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  return typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : '';
};

const toWire = (field: FormField, picked: string): unknown =>
  field.valueType === 'integer' ? Number(picked) : picked;

const isMultiple = (field: FormField): boolean =>
  field.multiple === true || field.type === 'multiselect';

const pickerPaper: SxProps<Theme> = theme => ({
  color: theme.palette.text.primary,
  '& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions': {
    color: mutedText(theme),
  },
});

const describedBy = (...ids: Array<string | undefined>): string | undefined => {
  const joined = ids.filter(id => id != null && id !== '').join(' ');
  return joined === '' ? undefined : joined;
};

export const isWideField = (field: FormField): boolean =>
  WIDE_TYPES.includes(field.type);

export const withDefaults = (
  fields: FormField[],
  values: Record<string, unknown>,
): Record<string, unknown> =>
  fields.reduce<Record<string, unknown>>(
    (current, field) =>
      field.default != null && getPath(current, field.key) === undefined
        ? setPath(current, field.key, field.default)
        : current,
    values,
  );

export const collectErrors = (
  fields: FormField[],
  values: Record<string, unknown>,
  prefix = '',
  visibility: Visibility = visibilityOf(fields, values),
): FieldErrors =>
  fields
    .filter(field => field.readOnly !== true && visibility.field(field))
    .reduce<FieldErrors>((errors, field) => {
      const key = `${prefix}${field.key}`;
      const value = getPath(values, field.key);
      const message = validateField(field, value);
      const withField =
        message === undefined ? errors : { ...errors, [key]: message };
      if (field.type !== 'repeater' || !Array.isArray(value)) {
        return withField;
      }
      const itemFields = field.itemFields ?? [];
      return value.reduce<FieldErrors>((rowErrors, item, index) => {
        const record = isRecord(item) ? item : {};
        return {
          ...rowErrors,
          ...collectErrors(
            itemFields,
            record,
            `${key}.${index}.`,
            itemVisibility(itemFields, record, visibility),
          ),
        };
      }, withField);
    }, {});

const omitPath = (
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> => {
  const [head, ...rest] = key.split('.');
  if (!Object.prototype.hasOwnProperty.call(record, head)) {
    return record;
  }
  if (rest.length === 0) {
    return Object.fromEntries(
      Object.entries(record).filter(([name]) => name !== head),
    );
  }
  const child = record[head];
  return isRecord(child)
    ? { ...record, [head]: omitPath(child, rest.join('.')) }
    : record;
};

const withoutHiddenItemFields = (
  field: FormField,
  value: unknown,
  visibility: Visibility,
): unknown => {
  if (field.type !== 'repeater' || !Array.isArray(value)) {
    return value;
  }
  const itemFields = field.itemFields ?? [];
  return value.map(item => {
    if (!isRecord(item)) {
      return item;
    }
    const shown = itemVisibility(itemFields, item, visibility);
    return itemFields
      .filter(itemField => !shown.field(itemField))
      .reduce((kept, itemField) => omitPath(kept, itemField.key), item);
  });
};

export const toSubmitValues = (
  fields: FormField[],
  values: Record<string, unknown>,
  visibility: Visibility = visibilityOf(fields, values),
): Record<string, unknown> =>
  Object.fromEntries(
    fields
      .filter(field => field.type !== 'note' && visibility.field(field))
      .map((field): [string, unknown] => [
        field.key,
        withoutHiddenItemFields(field, getPath(values, field.key), visibility),
      ])
      .filter(([, value]) => value !== undefined),
  );

const useLatest = <T,>(value: T): React.MutableRefObject<T> => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
};

const useFieldIds = (): { id: string; hintId: string; helperId: string } => {
  const base = `alpha-field-${useId().replace(/:/g, '')}`;
  return {
    id: base,
    hintId: `${base}-hint`,
    helperId: `${base}-helper-text`,
  };
};

const useRowKeys = (count: number): [string[], (next: string[]) => void] => {
  const [keys, setKeys] = useState<string[]>(() =>
    Array.from({ length: count }, nextRowKey),
  );
  const current = useMemo(
    () =>
      keys.length === count
        ? keys
        : [
            ...keys.slice(0, count),
            ...Array.from(
              { length: Math.max(0, count - keys.length) },
              nextRowKey,
            ),
          ],
    [keys, count],
  );
  useEffect(() => {
    if (current !== keys) {
      setKeys(current);
    }
  }, [current, keys]);
  return [current, setKeys];
};

interface OptionState {
  options: FormOption[];
  loading: boolean;
  failed: boolean;
  settled: boolean;
  retry: () => void;
}

const useFieldOptions = (
  field: FormField,
  loadOptions: LoadOptions,
  query: string,
  enabled: boolean,
  onOptions?: (target: FormField, options: FormOption[]) => void,
): OptionState => {
  const source = field.optionSource ?? undefined;
  const [loaded, setLoaded] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [settled, setSettled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const load = useLatest(loadOptions);
  const report = useLatest(onOptions);
  const fieldRef = useLatest(field);

  useEffect(() => {
    if (source == null || !enabled) {
      return undefined;
    }
    let live = true;
    const run = (): void => {
      setLoading(true);
      setFailed(false);
      setSettled(false);
      Promise.resolve()
        .then(() => load.current(source, query))
        .then(options => {
          if (!live) {
            return;
          }
          const capped = options.slice(0, MAX_OPTIONS);
          setLoaded(capped);
          setLoading(false);
          setSettled(true);
          report.current?.(fieldRef.current, capped);
        })
        .catch(() => {
          if (!live) {
            return;
          }
          setLoading(false);
          setFailed(true);
        });
    };
    if (query === '') {
      run();
      return () => {
        live = false;
      };
    }
    const timer = setTimeout(run, SEARCH_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [source, query, enabled, attempt, load, report, fieldRef]);

  const staticOptions = field.options ?? [];
  return {
    options: source == null ? staticOptions : loaded,
    loading: source != null && loading,
    failed: source != null && failed,
    settled: source == null || settled,
    retry: () => {
      setAttempt(count => count + 1);
    },
  };
};

const OptionsProblem = ({
  field,
  retry,
}: {
  field: FormField;
  retry: () => void;
}): JSX.Element => (
  <LoadProblem role="alert">
    <span>{`Couldn't load the options for ${field.label}.`}</span>
    <LinkButton type="button" onClick={retry}>
      Try again
    </LinkButton>
  </LoadProblem>
);

const TextInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
}: InputProps): JSX.Element => {
  const { id, hintId, helperId } = useFieldIds();
  const helper = error ?? field.help ?? undefined;
  const inputType = TEXT_TYPES[field.type] ?? 'text';
  const timeLike = inputType === 'date' || inputType === 'time';
  return (
    <>
      <TextField
        id={id}
        fullWidth
        size="small"
        label={field.label}
        type={inputType}
        value={toText(value)}
        required={field.required === true}
        error={error != null}
        helperText={helper}
        placeholder={field.placeholder ?? undefined}
        disabled={disabled}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 3 : undefined}
        maxRows={field.type === 'textarea' ? 12 : undefined}
        autoComplete={AUTOCOMPLETE[field.type] ?? 'off'}
        InputLabelProps={timeLike ? { shrink: true } : undefined}
        inputProps={{
          inputMode: field.type === 'phone' ? 'tel' : undefined,
          'aria-describedby': describedBy(
            helper != null ? helperId : undefined,
            hint != null ? hintId : undefined,
          ),
        }}
        onChange={event => {
          onChange(event.target.value === '' ? null : event.target.value);
        }}
        onBlur={onBlur}
      />
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const NumberInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
}: InputProps): JSX.Element => {
  const { id, hintId, helperId } = useFieldIds();
  const helper = error ?? field.help ?? undefined;
  const whole = field.type !== 'number';
  return (
    <>
      <TextField
        id={id}
        fullWidth
        size="small"
        type="number"
        label={field.label}
        value={toText(value)}
        required={field.required === true}
        error={error != null}
        helperText={helper}
        placeholder={field.placeholder ?? undefined}
        disabled={disabled}
        InputProps={{
          endAdornment:
            field.type === 'durationMinutes' ? (
              <InputAdornment position="end">min</InputAdornment>
            ) : undefined,
          inputProps: {
            min: field.min ?? undefined,
            max: field.max ?? undefined,
            step: whole ? 1 : 'any',
            inputMode: whole ? 'numeric' : 'decimal',
            'aria-describedby': describedBy(
              helper != null ? helperId : undefined,
              hint != null ? hintId : undefined,
            ),
          },
        }}
        onChange={event => {
          const raw = event.target.value.trim();
          const number = Number(raw);
          onChange(raw === '' || !Number.isFinite(number) ? null : number);
        }}
        onBlur={onBlur}
      />
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const ToggleInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
}: InputProps): JSX.Element => {
  const { id, hintId, helperId } = useFieldIds();
  const helper = error ?? field.help ?? undefined;
  return (
    <FormControl error={error != null} disabled={disabled}>
      <FormControlLabel
        label={field.label}
        control={
          <Switch
            id={id}
            checked={value === true}
            onChange={(_event, checked) => {
              onChange(checked);
            }}
            onBlur={onBlur}
            inputProps={{
              'aria-invalid': error != null,
              'aria-describedby': describedBy(
                helper != null ? helperId : undefined,
                hint != null ? hintId : undefined,
              ),
            }}
          />
        }
      />
      {helper != null && (
        <FormHelperText id={helperId}>{helper}</FormHelperText>
      )}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </FormControl>
  );
};

const SelectInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
  loadOptions,
  onOptions,
}: OptionInputProps): JSX.Element => {
  const { id, hintId, helperId } = useFieldIds();
  const { options, loading, failed, retry } = useFieldOptions(
    field,
    loadOptions,
    '',
    true,
    onOptions,
  );
  const current = toText(value);
  const listed =
    current === '' || options.some(option => option.value === current)
      ? options
      : [{ value: current, label: current }, ...options];
  const helper = error ?? field.help ?? undefined;
  return (
    <>
      <TextField
        id={id}
        select
        fullWidth
        size="small"
        label={field.label}
        value={current}
        required={field.required === true}
        error={error != null}
        helperText={helper}
        disabled={disabled}
        InputProps={
          loading
            ? {
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 3 }}>
                    <CircularProgress size={16} aria-label="Loading options" />
                  </InputAdornment>
                ),
              }
            : undefined
        }
        SelectProps={{
          inputProps: {
            'aria-describedby': describedBy(
              helper != null ? helperId : undefined,
              hint != null ? hintId : undefined,
            ),
          },
        }}
        onChange={event => {
          onChange(
            event.target.value === ''
              ? null
              : toWire(field, event.target.value),
          );
        }}
        onBlur={onBlur}
      >
        {field.required !== true && <MenuItem value="">None</MenuItem>}
        {listed.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <OptionLabel>
              <span>{option.label}</span>
              {option.detail != null && option.detail !== '' && (
                <OptionDetail>{option.detail}</OptionDetail>
              )}
            </OptionLabel>
          </MenuItem>
        ))}
      </TextField>
      {failed && <OptionsProblem field={field} retry={retry} />}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const optionSignature = (choices: FormOption[]): string =>
  choices
    .map(choice => `${choice.value}=${choice.label}=${choice.detail ?? ''}`)
    .join('\n');

const PickerInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
  loadOptions,
  onOptions,
}: OptionInputProps): JSX.Element => {
  const { hintId } = useFieldIds();
  const multiple = isMultiple(field);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [inputText, setInputText] = useState('');
  const [known, setKnown] = useState<Record<string, FormOption>>({});
  const selectedValues = toArray(value).map(item => String(item));
  const { options, loading, failed, retry } = useFieldOptions(
    field,
    loadOptions,
    query,
    open || selectedValues.length > 0,
    onOptions,
  );

  useEffect(() => {
    if (options.length > 0) {
      setKnown(previous => ({
        ...previous,
        ...Object.fromEntries(options.map(option => [option.value, option])),
      }));
    }
  }, [options]);

  const resolved = selectedValues.map(
    item =>
      options.find(option => option.value === item) ??
      known[item] ?? { value: item, label: item },
  );
  const stable = useRef({ signature: '', choices: resolved });
  const signature = optionSignature(resolved);
  if (stable.current.signature !== signature) {
    stable.current = { signature, choices: resolved };
  }
  const selected = stable.current.choices;
  const single = selected.length > 0 ? selected[0] : null;
  const listed = [
    ...selected.filter(
      choice => !options.some(option => option.value === choice.value),
    ),
    ...options,
  ];
  const helper = error ?? field.help ?? undefined;
  const serverFiltered = field.optionSource != null;

  return (
    <>
      <Autocomplete<FormOption, boolean, false, false>
        multiple={multiple}
        disabled={disabled}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          setQuery('');
          if (multiple) {
            setInputText('');
          }
        }}
        options={listed}
        value={multiple ? selected : single}
        inputValue={inputText}
        loading={loading}
        loadingText="Loading…"
        noOptionsText={failed ? 'The list did not load.' : 'No matches'}
        filterOptions={serverFiltered ? items => items : undefined}
        filterSelectedOptions={multiple}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, choice) => option.value === choice.value}
        slotProps={{ paper: { sx: pickerPaper } }}
        onInputChange={(_event, text, reason) => {
          setInputText(text);
          setQuery(reason === 'input' ? text : '');
        }}
        onChange={(_event, next) => {
          if (Array.isArray(next)) {
            onChange(next.map(option => toWire(field, option.value)));
            return;
          }
          onChange(next == null ? null : toWire(field, next.value));
        }}
        onBlur={onBlur}
        renderOption={({ key, ...optionProps }, option) => (
          <li {...optionProps} key={`${String(key)}-${option.value}`}>
            <OptionLabel>
              <span>{option.label}</span>
              {option.detail != null && option.detail !== '' && (
                <OptionDetail>{option.detail}</OptionDetail>
              )}
            </OptionLabel>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label={field.label}
            required={field.required === true}
            error={error != null}
            helperText={helper}
            placeholder={field.placeholder ?? undefined}
            inputProps={{
              ...params.inputProps,
              'aria-describedby': describedBy(
                helper != null ? `${params.id}-helper-text` : undefined,
                hint != null ? hintId : undefined,
              ),
            }}
          />
        )}
      />
      {failed && <OptionsProblem field={field} retry={retry} />}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const NumberListInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
}: InputProps): JSX.Element => {
  const { hintId } = useFieldIds();
  const helper = error ?? field.help ?? 'Type a number, then press Enter.';
  const chosen = toArray(value).map(item => String(item));
  return (
    <>
      <Autocomplete<string, true, false, true>
        multiple
        freeSolo
        autoSelect
        disabled={disabled}
        options={[]}
        value={chosen}
        onChange={(_event, next) => {
          const numbers = next
            .map(item => Number(String(item).trim()))
            .filter(
              (item, index, all) =>
                Number.isFinite(item) && all.indexOf(item) === index,
            );
          onChange(numbers);
        }}
        onBlur={onBlur}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label={field.label}
            required={field.required === true}
            error={error != null}
            helperText={helper}
            inputProps={{
              ...params.inputProps,
              inputMode: 'decimal',
              'aria-describedby': describedBy(
                `${params.id}-helper-text`,
                hint != null ? hintId : undefined,
              ),
            }}
          />
        )}
      />
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const toHours = (value: unknown): HoursValue[] =>
  toArray(value)
    .filter(isRecord)
    .map(row => ({
      ...row,
      day: typeof row.day === 'string' ? row.day : 'mon',
      start: typeof row.start === 'string' ? row.start : '',
      end: typeof row.end === 'string' ? row.end : '',
    }));

const WeeklyHoursInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
}: InputProps): JSX.Element => {
  const { hintId, helperId } = useFieldIds();
  const rows = toHours(value);
  const [keys, setKeys] = useRowKeys(rows.length);
  const helper = error ?? field.help ?? undefined;

  const update = (index: number, patch: Partial<HoursValue>): void => {
    onChange(
      rows.map((row, at) => (at === index ? { ...row, ...patch } : row)),
    );
  };

  const add = (): void => {
    const used = rows.map(row => row.day);
    const day =
      WEEK_DAYS.find(option => !used.includes(option.value))?.value ?? 'mon';
    const last = rows[rows.length - 1];
    setKeys([...keys, nextRowKey()]);
    onChange([
      ...rows,
      { day, start: last?.start ?? '09:00', end: last?.end ?? '17:00' },
    ]);
  };

  const remove = (index: number): void => {
    setKeys(keys.filter((_key, at) => at !== index));
    onChange(rows.filter((_row, at) => at !== index));
  };

  return (
    <FieldSet
      data-alpha-invalid={error != null ? 'true' : undefined}
      tabIndex={error != null ? -1 : undefined}
      aria-describedby={describedBy(
        helper != null ? helperId : undefined,
        hint != null ? hintId : undefined,
      )}
    >
      <legend>
        {field.label}
        {field.required === true ? ' *' : ''}
      </legend>
      {rows.length === 0 && <EmptyLine>No hours yet.</EmptyLine>}
      {rows.map((row, index) => (
        <HoursRow
          key={keys[index]}
          role="group"
          aria-label={`${field.label} ${index + 1}`}
        >
          <TextField
            className="alpha-hours-day"
            select
            size="small"
            label="Day"
            value={row.day}
            disabled={disabled}
            onChange={event => {
              update(index, { day: event.target.value });
            }}
            onBlur={onBlur}
          >
            {WEEK_DAYS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className="alpha-hours-time"
            size="small"
            type="time"
            label="Start"
            value={row.start}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            onChange={event => {
              update(index, { start: event.target.value });
            }}
            onBlur={onBlur}
          />
          <TextField
            className="alpha-hours-time"
            size="small"
            type="time"
            label="End"
            value={row.end}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            onChange={event => {
              update(index, { end: event.target.value });
            }}
            onBlur={onBlur}
          />
          <SecondaryButton
            type="button"
            disabled={disabled}
            aria-label={`Remove ${dayLabel(row.day)} ${row.start}–${row.end}`}
            onClick={() => {
              remove(index);
            }}
          >
            Remove
          </SecondaryButton>
        </HoursRow>
      ))}
      <div>
        <SecondaryButton type="button" disabled={disabled} onClick={add}>
          Add hours
        </SecondaryButton>
      </div>
      {helper != null && (
        <FormHelperText id={helperId} error={error != null}>
          {helper}
        </FormHelperText>
      )}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </FieldSet>
  );
};

interface RepeaterProps extends InputProps {
  errorKey: string;
  errors: FieldErrors;
  renderField: (props: FieldRendererProps) => JSX.Element;
  loadOptions: LoadOptions;
  onOptions?: (field: FormField, options: FormOption[]) => void;
  scope?: Visibility;
}

const RepeaterInput = ({
  field,
  value,
  error,
  hint,
  disabled,
  onChange,
  onBlur,
  errorKey,
  errors,
  renderField,
  loadOptions,
  onOptions,
  scope,
}: RepeaterProps): JSX.Element => {
  const { hintId, helperId } = useFieldIds();
  const items = toArray(value).map(item => (isRecord(item) ? item : {}));
  const itemFields = field.itemFields ?? [];
  const outer = scope ?? visibilityOf([], {});
  const [keys, setKeys] = useRowKeys(items.length);
  const helper = error ?? field.help ?? undefined;

  const add = (): void => {
    setKeys([...keys, nextRowKey()]);
    onChange([...items, withDefaults(itemFields, {})]);
  };

  const remove = (index: number): void => {
    setKeys(keys.filter((_key, at) => at !== index));
    onChange(items.filter((_item, at) => at !== index));
  };

  return (
    <FieldSet
      data-alpha-invalid={error != null ? 'true' : undefined}
      tabIndex={error != null ? -1 : undefined}
      aria-describedby={describedBy(
        helper != null ? helperId : undefined,
        hint != null ? hintId : undefined,
      )}
    >
      <legend>
        {field.label}
        {field.required === true ? ' *' : ''}
      </legend>
      {items.length === 0 && (
        <EmptyLine>{`No ${field.label.toLowerCase()} yet.`}</EmptyLine>
      )}
      {items.map((item, index) => {
        const shown = itemVisibility(itemFields, item, outer);
        return (
          <RowCard
            key={keys[index]}
            role="group"
            aria-label={`${field.label} ${index + 1}`}
          >
            <RowHeader>
              <span>{`Item ${index + 1}`}</span>
              <SecondaryButton
                type="button"
                disabled={disabled}
                aria-label={`Remove item ${index + 1}`}
                onClick={() => {
                  remove(index);
                }}
              >
                Remove
              </SecondaryButton>
            </RowHeader>
            <FieldGrid>
              {itemFields
                .filter(itemField => shown.field(itemField))
                .map(itemField => (
                  <FieldCell key={itemField.key} wide={isWideField(itemField)}>
                    {renderField({
                      field: itemField,
                      value: getPath(item, itemField.key),
                      errors,
                      errorKey: `${errorKey}.${index}.${itemField.key}`,
                      disabled,
                      loadOptions,
                      onOptions,
                      scope: shown,
                      onBlur,
                      onChange: next => {
                        onChange(
                          items.map((row, at) =>
                            at === index
                              ? setPath(row, itemField.key, next)
                              : row,
                          ),
                        );
                      },
                    })}
                  </FieldCell>
                ))}
            </FieldGrid>
          </RowCard>
        );
      })}
      <div>
        <SecondaryButton type="button" disabled={disabled} onClick={add}>
          Add
        </SecondaryButton>
      </div>
      {helper != null && (
        <FormHelperText id={helperId} error={error != null}>
          {helper}
        </FormHelperText>
      )}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </FieldSet>
  );
};

const labelOf = (
  field: FormField,
  item: unknown,
  labels: OptionLabels,
): string | undefined => {
  const key = String(item);
  return (
    field.options?.find(option => option.value === key)?.label ??
    labels[field.key]?.[key]
  );
};

interface ReadOnlyValueProps {
  field: FormField;
  value: unknown;
  loadOptions: LoadOptions;
  onOptions?: (field: FormField, options: FormOption[]) => void;
  scope?: Visibility;
  prefill?: Record<string, unknown>;
}

const useReadOnlyText = ({
  field,
  value,
  loadOptions,
  onOptions,
  scope,
  prefill,
}: ReadOnlyValueProps): {
  shown: React.ReactNode;
  problem: JSX.Element | null;
} => {
  const items = toArray(value);
  const lookup = field.optionSource != null && items.length > 0;
  const { options, loading, failed, settled, retry } = useFieldOptions(
    field,
    loadOptions,
    '',
    lookup,
    onOptions,
  );
  const labels: OptionLabels = {
    [field.key]: Object.fromEntries(
      options.map(option => [option.value, option.label]),
    ),
  };
  const own = ownLabels([field], prefill)[field.key] ?? {};
  const unresolved =
    lookup && items.some(item => labelOf(field, item, labels) === undefined);
  const fallbacks = items.map(
    item =>
      labelOf(field, item, labels) ?? own[String(item)] ?? NOT_IN_LIST_TEXT,
  );
  const problem =
    unresolved && failed ? (
      <OptionsProblem field={field} retry={retry} />
    ) : null;
  if (!unresolved) {
    const lines = displayLines(
      field,
      value,
      labels,
      scope ?? visibilityOf([field], {}),
    );
    return {
      shown: lines.length === 0 ? <NotSet>Not set</NotSet> : lines.join('\n'),
      problem,
    };
  }
  if (failed) {
    return {
      shown: fallbacks.includes(NOT_IN_LIST_TEXT) ? '' : fallbacks.join(', '),
      problem,
    };
  }
  return {
    shown: loading || !settled ? 'Loading…' : fallbacks.join(', '),
    problem,
  };
};

export const ReadOnlyValue = ({
  field,
  value,
  loadOptions,
  onOptions,
  scope,
  prefill,
}: ReadOnlyValueProps): JSX.Element => {
  const { shown, problem } = useReadOnlyText({
    field,
    value,
    loadOptions,
    onOptions,
    scope,
    prefill,
  });
  return (
    <>
      {shown}
      {problem}
    </>
  );
};

const ReadOnlyInput = ({
  field,
  value,
  error,
  hint,
  loadOptions,
  onOptions,
  scope,
  prefill,
}: OptionInputProps & {
  scope?: Visibility;
  prefill?: Record<string, unknown>;
}): JSX.Element => {
  const { hintId, helperId } = useFieldIds();
  const { shown, problem } = useReadOnlyText({
    field,
    value,
    loadOptions,
    onOptions,
    scope,
    prefill,
  });
  const helper = error ?? field.help ?? undefined;
  return (
    <>
      <ReadOnlyList data-alpha-readonly="">
        <dt>{field.label}</dt>
        <dd>{shown}</dd>
      </ReadOnlyList>
      {helper != null && (
        <FormHelperText id={helperId} error={error != null}>
          {helper}
        </FormHelperText>
      )}
      {problem}
      {hint != null && <FieldHint id={hintId}>{hint}</FieldHint>}
    </>
  );
};

const NoteField = ({ field }: { field: FormField }): JSX.Element => (
  <FieldNote>
    <p>{field.label}</p>
    {field.help != null && field.help !== '' && <p>{field.help}</p>}
  </FieldNote>
);

export const AlphaFieldRenderer = ({
  field,
  value,
  errors,
  errorKey,
  hint,
  disabled,
  onChange,
  onBlur,
  loadOptions,
  onOptions,
  scope,
  prefill,
}: FieldRendererProps): JSX.Element => {
  const ownKey = errorKey ?? field.key;
  const error = errors[ownKey];
  const input = { field, value, error, hint, disabled, onChange, onBlur };
  if (field.readOnly === true && field.type !== 'note') {
    return (
      <ReadOnlyInput
        {...input}
        loadOptions={loadOptions}
        onOptions={onOptions}
        scope={scope}
        prefill={prefill}
      />
    );
  }
  switch (field.type) {
    case 'number':
    case 'integer':
    case 'durationMinutes':
      return <NumberInput {...input} />;
    case 'toggle':
      return <ToggleInput {...input} />;
    case 'select':
      return field.multiple === true ? (
        <PickerInput
          {...input}
          loadOptions={loadOptions}
          onOptions={onOptions}
        />
      ) : (
        <SelectInput
          {...input}
          loadOptions={loadOptions}
          onOptions={onOptions}
        />
      );
    case 'multiselect':
    case 'userPicker':
    case 'entityPicker':
      return (
        <PickerInput
          {...input}
          loadOptions={loadOptions}
          onOptions={onOptions}
        />
      );
    case 'numberList':
      return <NumberListInput {...input} />;
    case 'weeklyHours':
      return <WeeklyHoursInput {...input} />;
    case 'repeater':
      return (
        <RepeaterInput
          {...input}
          errorKey={ownKey}
          errors={errors}
          loadOptions={loadOptions}
          onOptions={onOptions}
          scope={scope}
          renderField={itemProps => <AlphaFieldRenderer {...itemProps} />}
        />
      );
    case 'note':
      return <NoteField field={field} />;
    default:
      return <TextInput {...input} />;
  }
};
